import { isNonEmptyString } from 'shared/utils/is-non-empty-string'
import { defaultBrandSettings } from '../constants/brand-settings'
import { BrandColors, BrandSettingsInput, BrandSettingsInterface } from '../types/brand-settings'

type BrandAsset = NonNullable<BrandSettingsInterface['logo']>

const cloneColors = (colors: BrandColors): BrandColors => ({
  oldBrandPrimary: colors.oldBrandPrimary,
  oldTextPrimary: colors.oldTextPrimary,
  oldBackgroundPrimary: colors.oldBackgroundPrimary,
  oldBackgroundHeader: colors.oldBackgroundHeader,
})

const cloneAsset = (asset: BrandAsset): BrandAsset => ({
  id: asset.id,
  path: asset.path,
  filename: asset.filename,
  name: asset.name,
  thumbnail: asset.thumbnail ?? null,
})

const normalizeColors = (
  candidate: BrandSettingsInput['colors'],
  fallback: BrandColors,
): BrandColors => {
  if (!candidate || typeof candidate !== 'object') {
    return cloneColors(fallback)
  }

  const colors = candidate

  return {
    oldBrandPrimary: isNonEmptyString(colors.oldBrandPrimary)
      ? colors.oldBrandPrimary
      : fallback.oldBrandPrimary,
    oldTextPrimary: isNonEmptyString(colors.oldTextPrimary)
      ? colors.oldTextPrimary
      : fallback.oldTextPrimary,
    oldBackgroundPrimary: isNonEmptyString(colors.oldBackgroundPrimary)
      ? colors.oldBackgroundPrimary
      : fallback.oldBackgroundPrimary,
    oldBackgroundHeader: isNonEmptyString(colors.oldBackgroundHeader)
      ? colors.oldBackgroundHeader
      : fallback.oldBackgroundHeader,
  }
}

const normalizeAsset = (
  candidate: BrandSettingsInput['logo'],
  fallback: BrandAsset | null,
): BrandAsset | null => {
  if (!candidate || typeof candidate !== 'object') {
    return fallback ? cloneAsset(fallback) : null
  }

  const asset = candidate as Partial<Record<keyof BrandAsset, unknown>>

  if (
    !isNonEmptyString(asset.path) ||
    !isNonEmptyString(asset.filename) ||
    !isNonEmptyString(asset.name)
  ) {
    return fallback ? cloneAsset(fallback) : null
  }

  return {
    id: typeof asset.id === 'number' ? asset.id : (fallback?.id ?? 0),
    path: asset.path,
    filename: asset.filename,
    name: asset.name,
    thumbnail: isNonEmptyString(asset.thumbnail) ? asset.thumbnail : (fallback?.thumbnail ?? null),
  }
}

export const normalizeBrandSettings = (
  candidate: BrandSettingsInput | null | undefined,
  fallbackBrand: BrandSettingsInterface = defaultBrandSettings,
): BrandSettingsInterface => {
  const fallback = fallbackBrand ?? defaultBrandSettings

  const fallbackLogo = fallback.logo ? cloneAsset(fallback.logo) : null
  const fallbackFavicon = fallback.favicon ? cloneAsset(fallback.favicon) : null
  const fallbackHostname = fallback.dashboardHostname

  if (!candidate || typeof candidate !== 'object') {
    return {
      name: fallback.name,
      dashboardHostname: fallbackHostname,
      colors: cloneColors(fallback.colors),
      logo: fallbackLogo,
      favicon: fallbackFavicon,
    }
  }

  const brand = candidate

  return {
    name: isNonEmptyString(brand.name) ? brand.name : fallback.name,
    dashboardHostname: isNonEmptyString(brand.dashboardHostname)
      ? brand.dashboardHostname
      : fallbackHostname,
    colors: normalizeColors(brand.colors, fallback.colors),
    logo: normalizeAsset(brand.logo, fallbackLogo),
    favicon: normalizeAsset(brand.favicon, fallbackFavicon),
  }
}
